import React, { forwardRef } from 'react';
import { cn } from '@/src/helpers';

interface FlexProps {
  as?: React.ElementType;
  direction?: 'row' | 'col' | 'row-reverse' | 'col-reverse';
  align?: 'start' | 'end' | 'center' | 'baseline' | 'stretch';
  justify?:
    | 'start'
    | 'end'
    | 'center'
    | 'between'
    | 'around'
    | 'evenly'
    | 'stretch';
  wrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  gap?: string | number; // e.g., '4', '1rem', or Tailwind spacing scale
  grow?: boolean | string | number; // e.g., true, '1', or number
  shrink?: boolean | string | number;
  basis?: string | number; // e.g., 'auto', '100px', '50%'
  className?: string;
}

// Utility to map props to Tailwind classes
const getFlexClasses = ({
  direction,
  align,
  justify,
  wrap,
  gap,
  grow,
  shrink,
  basis,
}: FlexProps) => {
  const classes = [
    'flex', // Base flex class
    direction ? `flex-${direction}` : 'flex-row', // Default to row
    align ? `items-${align}` : '',
    justify
      ? justify === 'between'
        ? 'justify-between'
        : justify === 'around'
          ? 'justify-around'
          : justify === 'evenly'
            ? 'justify-evenly'
            : `justify-${justify}`
      : '',
    wrap ? `flex-${wrap}` : '',
    gap ? `gap-${gap}` : '',
    grow === true
      ? 'grow'
      : typeof grow === 'string' || typeof grow === 'number'
        ? `grow-[${grow}]`
        : '',
    shrink === true
      ? 'shrink'
      : typeof shrink === 'string' || typeof shrink === 'number'
        ? `shrink-[${shrink}]`
        : '',
    basis
      ? typeof basis === 'number'
        ? `basis-${basis}`
        : `basis-[${basis}]`
      : '',
  ];

  return classes.filter(Boolean).join(' ');
};

// Flex component with forwardRef and polymorphic typing
const Flex = forwardRef<
  HTMLElement,
  FlexProps & React.HTMLAttributes<HTMLElement>
>(
  (
    {
      as: Component = 'div',
      direction,
      align,
      justify,
      wrap,
      gap,
      grow,
      shrink,
      basis,
      className,
      ...props
    },
    ref
  ) => {
    const flexClasses = getFlexClasses({
      direction,
      align,
      justify,
      wrap,
      gap,
      grow,
      shrink,
      basis,
    });

    return (
      <Component ref={ref} className={cn(flexClasses, className)} {...props} />
    );
  }
);

Flex.displayName = 'Flex';

export { Flex };
